import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { API_IP } from "../global_variables";
import "./Auth.css";

const STEPS = [
  { key: "email", title: "Your email", sub: "Where to send the code" },
  { key: "code", title: "Verify", sub: "Enter the 6-digit code" },
  { key: "password", title: "New password", sub: "Choose a new one" },
];

const stageIndex = (stage) => {
  const i = STEPS.findIndex((s) => s.key === stage);
  return i === -1 ? STEPS.length : i;
};

const maskEmail = (email) => {
  const [name, domain] = email.split("@");
  if (!domain) return email;
  const head = name.slice(0, 1);
  return `${head}${"•".repeat(Math.max(name.length - 1, 1))}@${domain}`;
};

const passwordChecks = (pwd) => ({
  length: pwd.length >= 8,
  letter: /[A-Za-z]/.test(pwd),
  number: /\d/.test(pwd),
});

const Stepper = ({ current }) => (
  <div className="AuthStepper">
    {STEPS.map((step, i) => {
      const state = i < current ? "isDone" : i === current ? "isActive" : "";
      return (
        <div className={`AuthStepItem ${state}`} key={step.key}>
          <span className="AuthStepIndex">{i < current ? "✓" : i + 1}</span>
          <span className="AuthStepLabel">
            <span className="AuthStepLabelTitle">{step.title}</span>
            <span className="AuthStepLabelSub">{step.sub}</span>
          </span>
        </div>
      );
    })}
  </div>
);

const EmailStage = ({ onSent, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_IP}/api/password`, { email: value });
    } catch (error) {
      console.error(error);
    } finally {
      setBusy(false);
      onSent(value);
    }
  };

  return (
    <form className="AuthForm AuthFadeInUp" onSubmit={submit} key="email">
      <div className="AuthField">
        <label htmlFor="forgotEmail">Email address</label>
        <input
          id="forgotEmail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
        />
      </div>
      <p className="AuthMetaText">
        We’ll email you a 6-digit code to confirm it’s really you. For your
        security, we won’t reveal whether an account exists.
      </p>
      <button type="submit" className="AuthPrimaryButton" disabled={busy}>
        {busy ? "Sending…" : "Send code"}
      </button>
      <div className="AuthFooterRow">
        <span>Remember it?</span>
        <a className="AuthLink" href="/login">
          Back to sign in
        </a>
      </div>
    </form>
  );
};

const CodeStage = ({ email, onVerified, onResend, onBack }) => {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);
  const submitting = useRef(false);

  useEffect(() => {
    inputs.current[0] && inputs.current[0].focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (code) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      await axios.post(`${API_IP}/api/password/verify`, { email, code });
      onVerified(code);
    } catch (err) {
      const status = err.response && err.response.status;
      toast.error(
        status === 429
          ? "Too many attempts. Please request a new code."
          : "That code is invalid or expired. Please try again."
      );
      setDigits(Array(6).fill(""));
      inputs.current[0] && inputs.current[0].focus();
    } finally {
      setBusy(false);
      submitting.current = false;
    }
  };

  const setDigit = (index, value) => {
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    const code = next.join("");
    if (code.length === 6 && !next.includes("")) verify(code);
  };

  const handleChange = (index, raw) => {
    const value = raw.replace(/\D/g, "");
    if (!value) {
      setDigit(index, "");
      return;
    }
    setDigit(index, value[value.length - 1]);
    if (index < 5) inputs.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = Array(6).fill("");
    text.split("").forEach((d, i) => (next[i] = d));
    setDigits(next);
    const target = Math.min(text.length, 5);
    inputs.current[target].focus();
    if (text.length === 6) verify(text);
  };

  const resend = async () => {
    if (cooldown > 0) return;
    await onResend();
    setCooldown(30);
  };

  const code = digits.join("");

  return (
    <div className="AuthForm AuthFadeInUp" key="code">
      <p className="AuthMetaText">
        If an account exists for{" "}
        <span className="AuthEmailChip">{maskEmail(email)}</span>, we’ve sent a
        6-digit code. It expires in 15 minutes.
      </p>
      <div
        className="AuthOtpRow"
        onPaste={handlePaste}
        role="group"
        aria-label="Verification code"
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputs.current[i] = el)}
            className={`AuthOtpInput ${digit ? "isFilled" : ""}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={busy}
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>
      <button
        type="button"
        className="AuthPrimaryButton"
        disabled={busy || code.length !== 6}
        onClick={() => verify(code)}
      >
        {busy ? "Verifying…" : "Verify code"}
      </button>
      <div className="AuthResendRow">
        <span>Didn’t get it?</span>
        <button
          type="button"
          className="AuthResendBtn"
          onClick={resend}
          disabled={cooldown > 0}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
      <button type="button" className="AuthSecondaryButton" onClick={onBack}>
        Use a different email
      </button>
    </div>
  );
};

const PasswordStage = ({ email, code, onDone, onExpired }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const checks = passwordChecks(password);
  const strong = checks.length && checks.letter && checks.number;
  const matches = confirm.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) {
      toast.error("Please meet all password requirements.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API_IP}/api/password`, {
        email,
        code,
        newPassword: password,
      });
      onDone();
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 429) {
        toast.error("Too many attempts. Please request a new code.");
      } else {
        toast.error("Your code has expired. Please request a new one.");
        onExpired();
      }
    } finally {
      setBusy(false);
    }
  };

  const req = (ok, label) => (
    <div className={`AuthReqItem ${ok ? "ok" : ""}`}>
      <span className="AuthReqTick">{ok ? "✓" : "•"}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <form className="AuthForm AuthFadeInUp" onSubmit={submit} key="password">
      <div className="AuthField">
        <label htmlFor="newPassword">New password</label>
        <div className="AuthInputWithAction">
          <input
            id="newPassword"
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a new password"
            autoComplete="new-password"
            autoFocus
          />
          <button
            type="button"
            className="AuthInputAction"
            onClick={() => setShow((v) => !v)}
          >
            {show ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      <div className="AuthReqList">
        {req(checks.length, "At least 8 characters")}
        {req(checks.letter, "Contains a letter")}
        {req(checks.number, "Contains a number")}
      </div>

      <div className="AuthField">
        <label htmlFor="confirmPassword">Confirm new password</label>
        <input
          id="confirmPassword"
          type={show ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter your new password"
          autoComplete="new-password"
        />
      </div>
      {confirm.length > 0 && (
        <div className={`AuthReqItem ${matches ? "ok" : ""}`}>
          <span className="AuthReqTick">{matches ? "✓" : "•"}</span>
          <span>{matches ? "Passwords match" : "Passwords don’t match yet"}</span>
        </div>
      )}

      <button
        type="submit"
        className="AuthPrimaryButton"
        disabled={busy || !strong || !matches}
      >
        {busy ? "Saving…" : "Reset password"}
      </button>
    </form>
  );
};

const DoneStage = ({ onSignIn }) => (
  <div className="AuthSuccess AuthFadeInUp" key="done">
    <div className="AuthSuccessIcon">✓</div>
    <div className="AuthHeader">
      <h1>All set</h1>
      <p>Your password has been reset. You can now sign in with it.</p>
    </div>
    <button type="button" className="AuthPrimaryButton" onClick={onSignIn}>
      Go to sign in
    </button>
  </div>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const paneRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(null);

  useLayoutEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const measure = () => setPaneHeight(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stage]);

  const sendCode = async (value) => {
    try {
      await axios.post(`${API_IP}/api/password`, { email: value });
      toast.success("Code sent. Please check your inbox.");
    } catch (error) {
      console.error(error);
    }
  };

  const current = stageIndex(stage);
  const heading =
    stage === "email"
      ? { h: "Forgot your password?", p: "Enter your email and we’ll send you a recovery code." }
      : stage === "code"
      ? { h: "Check your email", p: "Enter the 6-digit code we just sent you." }
      : { h: "Set a new password", p: "Almost done — choose a strong new password." };

  return (
    <div className="contentContainer">
      <div className="AuthShell AuthPageEnter">
        <div className="AuthCard">
          <div className="AuthLeftPane">
            <div className="AuthBrand">
              <img
                src={require("./../images/iCareVillage_logo.webp")}
                alt="iCareVillage"
                className="AuthBrandLogo"
              />
              <div>
                <div className="AuthBrandName">iCare Village</div>
                <div className="AuthBrandTag">
                  Care made simple, for you and your family.
                </div>
              </div>
            </div>
            <Stepper current={current} />
          </div>

          <div className="AuthRightPane">
            {stage !== "done" && (
              <span className="AuthHeaderBadge">
                Step {current + 1} of {STEPS.length}
              </span>
            )}
            {stage !== "done" && (
              <div className="AuthHeader">
                <h1>{heading.h}</h1>
                <p>{heading.p}</p>
              </div>
            )}

            <div
              className="AuthPaneMotion"
              style={paneHeight ? { height: paneHeight } : undefined}
            >
              <div ref={paneRef} className="AuthPaneInner">
                {stage === "email" && (
                  <EmailStage
                    initialEmail={email}
                    onSent={(value) => {
                      setEmail(value);
                      setStage("code");
                    }}
                  />
                )}
                {stage === "code" && (
                  <CodeStage
                    email={email}
                    onResend={() => sendCode(email)}
                    onBack={() => setStage("email")}
                    onVerified={(value) => {
                      setCode(value);
                      setStage("password");
                    }}
                  />
                )}
                {stage === "password" && (
                  <PasswordStage
                    email={email}
                    code={code}
                    onExpired={() => setStage("code")}
                    onDone={() => setStage("done")}
                  />
                )}
                {stage === "done" && (
                  <DoneStage onSignIn={() => navigate("/login")} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
